import React from 'react';

// Internal modules
import { Badge } from '@paypalcorp/pp-react-badge-weasley-v6';
import { CheckCircleAltIcon } from '@paypalcorp/pp-react-icons-weasley-v6';
import {
  ExpiryDate,
  IncentiveCardV1,
  IncentiveCardV2,
  IncentiveCol,
  IncentiveRow,
  OfferAcceptedV3Background,
  OfferAcceptedV3CheckCircleAltIcon,
  OfferAcceptedV3Header,
  OfferAcceptedV3Text1,
  OfferV1Description,
  OfferV2Description,
  OfferV3Description,
  SuccessTermsLinkText,
  SuccessTermsLinkTextV3,
  SuccessText,
  TermsLinkTextV1,
  TermsLinkTextV2,
} from './StyledComponents';
import { OfferCSSV1, OfferCSSV2, OfferCSSV3 } from './Icons';
import { buildTermsUrl, getContent } from '@/lib';

type CurrencySymbolProp = {
  currencySymbol?: string | null;
};

const CheckoutIncentiveV1 = ({
  currencySymbol,
}: CurrencySymbolProp): JSX.Element => {
  return (
    <IncentiveCardV1>
      <IncentiveRow>
        <OfferCSSV1 />
        <OfferV1Description data-testid="offer-description-v1">
          {getContent('checkoutIncentives.description1', { currencySymbol })}{' '}
          <TermLinkV1
            contentKey="checkoutIncentives.termsLink"
            path="campaigns/nna/signupcheckout"
            testid="terms-link-v1"
          />
        </OfferV1Description>
      </IncentiveRow>
    </IncentiveCardV1>
  );
};

const CheckoutIncentiveV2 = ({
  currencySymbol,
}: CurrencySymbolProp): JSX.Element => {
  return (
    <IncentiveCardV2>
      <IncentiveRow>
        <OfferCSSV2 />
        <OfferV2Description data-testid="offer-description-v2">
          {getContent('checkoutIncentives.description1', { currencySymbol })}{' '}
          <TermLinkV2
            contentKey="checkoutIncentives.termsLink"
            path="campaigns/nna/signupcheckout"
            testid="terms-link-v2"
          />
        </OfferV2Description>
      </IncentiveRow>
    </IncentiveCardV2>
  );
};

const CheckoutIncentiveV3 = ({
  currencySymbol,
}: CurrencySymbolProp): JSX.Element => {
  return (
    <IncentiveCardV2>
      <IncentiveRow>
        <OfferCSSV3 currencySymbol={currencySymbol} />
      </IncentiveRow>
      <IncentiveRow>
        <OfferV3Description data-testid="offer-description-v3">
          {getContent('checkoutIncentives.delayed.v3.description1', {
            currencySymbol,
          })}{' '}
          <TermLinkV2
            contentKey="checkoutIncentives.termsLink"
            path="campaigns/nna/signupcheckout"
            testid="terms-link-v3"
          />
        </OfferV3Description>
      </IncentiveRow>
    </IncentiveCardV2>
  );
};

const SuccessV1 = ({ currencySymbol }: CurrencySymbolProp): JSX.Element => {
  const expiryDate = (
    <ExpiryDate>
      {getContent('checkoutIncentives.delayed.expiryDate')}
    </ExpiryDate>
  );

  return (
    <IncentiveCol data-testid="offer-accepted-v1">
      <Badge type="success" leftIcon={CheckCircleAltIcon} theme="v2">
        {getContent('checkoutIncentives.offerAcceptedHeader')}
      </Badge>
      <SuccessText data-testid="offer-accepted-text1">
        {getContent('checkoutIncentives.offerAcceptedText1', {
          currencySymbol,
        })}
      </SuccessText>
      <IncentiveRow>
        <SuccessText data-testid="offer-accepted-text2">
          {getContent('checkoutIncentives.offerAcceptedText2', {
            expiryDate,
          })}{' '}
          <SuccessTermsLink
            contentKey="checkoutIncentives.termsLink"
            path="campaigns/nna/signupcheckout"
            testid="terms-link"
          />
        </SuccessText>
      </IncentiveRow>
    </IncentiveCol>
  );
};

const SuccessV2 = ({ currencySymbol }: CurrencySymbolProp): JSX.Element => {
  return (
    <IncentiveCol data-testid="offer-accepted-v2">
      <Badge
        type="success"
        leftIcon={CheckCircleAltIcon}
        theme="v2"
        data-testid="offer-accepted-header"
      >
        {getContent('checkoutIncentives.offerAcceptedHeader')}
      </Badge>
      <SuccessText data-testid="offer-accepted-text1">
        {getContent('checkoutIncentives.delayed.offerAcceptedText1', {
          currencySymbol,
        })}
      </SuccessText>
      <IncentiveRow>
        <SuccessText data-testid="offer-accepted-text2">
          {getContent('checkoutIncentives.delayed.maxRewards')}{' '}
          <SuccessTermsLink
            contentKey="checkoutIncentives.termsLink"
            path="campaigns/nna/signupcheckout"
            testid="terms-link"
          />
        </SuccessText>
      </IncentiveRow>
    </IncentiveCol>
  );
};

const SuccessV3 = ({ currencySymbol }: CurrencySymbolProp): JSX.Element => {
  return (
    <IncentiveCol data-testid="offer-accepted-v3">
      <OfferAcceptedV3Background>
        <OfferAcceptedV3CheckCircleAltIcon />
        <OfferAcceptedV3Header data-testid="offer-accepted-header">
          {getContent('checkoutIncentives.delayed.v3.offerAcceptedHeader', {
            currencySymbol,
          })}
        </OfferAcceptedV3Header>
        <OfferAcceptedV3Text1 data-testid="offer-accepted-text1">
          {getContent('checkoutIncentives.delayed.offerAcceptedText1', {
            currencySymbol,
          })}{' '}
          {getContent('checkoutIncentives.delayed.maxRewards')}{' '}
        </OfferAcceptedV3Text1>
        <SuccessTermsLinkV3
          contentKey="checkoutIncentives.termsLink"
          path="campaigns/nna/signupcheckout"
          testid="terms-link"
        />
      </OfferAcceptedV3Background>
    </IncentiveCol>
  );
};

type TermLinkProps = {
  contentKey: string;
  path: string;
  testid: string;
};

const TermLinkV1 = ({
  contentKey,
  path,
  testid,
}: TermLinkProps): JSX.Element => {
  return (
    <TermsLinkTextV1
      data-testid={testid}
      href={buildTermsUrl({ path })}
      rel="noopener noreferrer"
      target="_blank"
    >
      {getContent(contentKey)}
    </TermsLinkTextV1>
  );
};

const TermLinkV2 = ({
  contentKey,
  path,
  testid,
}: TermLinkProps): JSX.Element => {
  return (
    <TermsLinkTextV2
      data-testid={testid}
      href={buildTermsUrl({ path })}
      rel="noopener noreferrer"
      target="_blank"
    >
      {getContent(contentKey)}
    </TermsLinkTextV2>
  );
};

const SuccessTermsLink = ({
  contentKey,
  path,
  testid,
}: TermLinkProps): JSX.Element => {
  return (
    <SuccessTermsLinkText
      data-testid={testid}
      href={buildTermsUrl({ path })}
      rel="noopener noreferrer"
      target="_blank"
    >
      {getContent(contentKey)}
    </SuccessTermsLinkText>
  );
};

const SuccessTermsLinkV3 = ({
  contentKey,
  path,
  testid,
}: TermLinkProps): JSX.Element => {
  return (
    <SuccessTermsLinkTextV3
      data-testid={testid}
      href={buildTermsUrl({ path })}
      rel="noopener noreferrer"
      target="_blank"
    >
      {getContent(contentKey)}
    </SuccessTermsLinkTextV3>
  );
};

export {
  CheckoutIncentiveV1,
  CheckoutIncentiveV2,
  CheckoutIncentiveV3,
  SuccessV1,
  SuccessV2,
  SuccessV3,
};
